import { Component, computed, EventEmitter, Input, OnDestroy, OnInit, Output, signal, Signal, WritableSignal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, Observable, Subscription, tap } from 'rxjs';
import { CommonModule } from '@angular/common';

import { ApContentModule, ApContentService } from '@ap/content';
import { ApInputComponent, InputType } from '@ap/input';
import { ErrorMessages } from '@ap/form/errors';
import { ApFormService } from '@ap/form';
import { ApWindowService } from '@ap/cdk';
import { ApError, ApErrorModule, ApErrorService } from '@ap/error';
import { ApButtonComponent } from '@ap/button';
import { ApIconComponent } from '@ap/icon';
import { ApIconLinkComponent } from '@ap/icon-link';
import { ApNotificationComponent } from '@ap/notification';

import content from './digicode-aanmaken.content.json';
import { IssuedDeviceManagementResource } from './resource/issued-device-management.resource';
import { PatchTokenRequest } from './resource/issued-device-management.model';
import { DigicodeAanmakenForm } from './digicode-aanmaken.model';
import { DigicodeAanmakenService } from './digicode-aanmaken.service';
import { Password } from './password';

@Component({
    selector: 'digicode-aanmaken',
    standalone: true,
    templateUrl: 'digicode-aanmaken.component.html',
    imports: [
        ApButtonComponent,
        ApErrorModule,
        ApContentModule,
        ApIconComponent,
        ApIconLinkComponent,
        ApInputComponent,
        ApNotificationComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule
    ],
    providers: [ApFormService, IssuedDeviceManagementResource]
})
export class DigicodeAanmakenComponent implements OnDestroy, OnInit {
    @Input()
    public tokenId: string;

    @Input()
    public tokenType: 'digicode' | 'prospect_digicode' = 'digicode';

    @Input()
    public digicodeAanmakenFormGroup: FormGroup<DigicodeAanmakenForm>;

    @Output()
    public creationResult: EventEmitter<string> = new EventEmitter();

    public errorMessagesUsername: ErrorMessages;
    public errorMessagesPassword: ErrorMessages;
    public errorMessagesValidatePassword: ErrorMessages;

    public password: Password = new Password();
    public showPasswordType: WritableSignal<InputType> = signal('password');
    public passwordCheckboxText: Signal<string> = computed(() =>
        this.contentService.get(`digicode-aanmaken.password.${this.showPasswordType()}.label`)
    );

    public inErrorState = false;
    protected apError$: Observable<ApError>;

    private readonly subscriptions: Subscription = new Subscription();
    private readonly USERNAME_IN_USE = 'LSM23025';
    private readonly SESSION_TIMEOUT = 'LSM23040';

    constructor(
        private readonly apErrorService: ApErrorService,
        private readonly apFormService: ApFormService,
        private readonly apWindowService: ApWindowService,
        private readonly contentService: ApContentService,
        private readonly issuedDeviceManagementResource: IssuedDeviceManagementResource
    ) {}

    ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.apError$ = this.apErrorService.error$();

        if (!this.digicodeAanmakenFormGroup) {
            this.digicodeAanmakenFormGroup = DigicodeAanmakenService.createDigicodeAanmakenFormGroup();
        }

        this.errorMessagesUsername = {
            required: this.contentService.get('digicode-aanmaken.changePassword.username.error.required'),
            alreadyExists: this.contentService.get('digicode-aanmaken.changePassword.username.error.alreadyExists'),
            minlength: this.contentService.get('digicode-aanmaken.changePassword.username.error.minlength'),
            maxlength: this.contentService.get('digicode-aanmaken.changePassword.username.error.maxlength'),
            pattern: this.contentService.get('digicode-aanmaken.changePassword.username.error.forbiddenCharacters')
        };

        this.errorMessagesPassword = {
            minlength: this.contentService.get('digicode-aanmaken.changePassword.password.error.minlength'),
            maxlength: this.contentService.get('digicode-aanmaken.changePassword.password.error.maxlength'),
            complexity: this.contentService.get('digicode-aanmaken.changePassword.password.error.complexity'),
            space: this.contentService.get('digicode-aanmaken.changePassword.password.error.space'),
            forbiddenCharacters: this.contentService.get('digicode-aanmaken.changePassword.password.error.forbiddenCharacters'),
            required: this.contentService.get('digicode-aanmaken.changePassword.password.error.required')
        };

        this.errorMessagesValidatePassword = {
            passwordsNotEqual: this.contentService.get('digicode-aanmaken.changePassword.validatePassword.error.passwordsNotEqual'),
            required: this.contentService.get('digicode-aanmaken.changePassword.validatePassword.error.required')
        };

        this.subscriptions.add(
            this.digicodeAanmakenFormGroup
                .get('password')
                .valueChanges.pipe(
                    debounceTime(300),
                    tap(() => 'Changed password')
                )
                .subscribe(() => {
                    this.passwordValueChanged();
                })
        );

        this.subscriptions.add(
            this.digicodeAanmakenFormGroup
                .get('validatePassword')
                .valueChanges.pipe(debounceTime(300))
                .subscribe(() => {
                    this.passwordValueChanged();
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public passwordValueChanged(): void {
        this.password = new Password(this.digicodeAanmakenFormGroup.value.password, this.digicodeAanmakenFormGroup.value.validatePassword);
    }

    public onSubmit(): void {
        this.digicodeAanmakenFormGroup.get('username').setErrors({ alreadyExists: false });
        const valid = this.apFormService.verify(this.digicodeAanmakenFormGroup);
        if (!valid) {
            return;
        }
        const patchTokenRequest: PatchTokenRequest = {
            credentials: {
                key_id: this.digicodeAanmakenFormGroup.get('username').getRawValue(),
                signature: this.digicodeAanmakenFormGroup.get('password').getRawValue()
            },
            token_type: this.tokenType
        };
        this.issuedDeviceManagementResource.patchToken(this.tokenId, patchTokenRequest).subscribe({
            next: () => this.creationResult.emit('OK'),
            error: (error) => {
                if (error.error?.code === this.USERNAME_IN_USE) {
                    this.digicodeAanmakenFormGroup.get('username').setErrors({ alreadyExists: true });
                } else if (error.error?.code === this.SESSION_TIMEOUT) {
                    this.inErrorState = true;
                    const errorTitle = this.contentService.get('digicode-aanmaken.session-expired.title');
                    const errorText = this.contentService.get('digicode-aanmaken.session-expired.text');
                    throw new ApError(errorText, 'digicode-aanmaken', new Error(`LSM23040`), true, errorTitle);
                } else {
                    this.creationResult.emit('NOK');
                }
            }
        });
    }

    public toggleType(e: Event): void {
        e.preventDefault();
        this.showPasswordType.update((type) => (type === 'password' ? 'text' : 'password'));
    }

    public reloadPage(): void {
        this.apWindowService.location_reload();
    }
}
